import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useLanguage, LANGS } from "../context/LanguageContext";
import { t3, pick3 } from "../i18n/v3";

/**
 * SidebarNav — V3.3 vertical navigation.
 *
 * Sidebar contains ONLY the lighthouse emblem (no wordmark, no tagline).
 * Navigation labels use serif editorial typography (Cormorant Garamond)
 * in title case, matching the literary voice of the site.
 */

const PRIMARY = [
    { key: "home",     to: "/" },
    { key: "ideas",    to: "/ideas" },
    { key: "research", to: "/research" },
    { key: "projects", to: "/projects" },
    { key: "memoire",  to: "/memoire" },
    { key: "about",    to: "/about" },
];

const SECONDARY = [
    { key: "philosophy", to: "/philosophy" },
    { key: "lighthouse", to: "/lighthouse" },
];

const TERTIARY = [
    { key: "contact", to: "/contact" },
];

function NavList({ items, onNavigate, lang }) {
    return (
        <ul className="flex flex-col gap-1">
            {items.map((it) => (
                <li key={it.key}>
                    <NavLink
                        to={it.to}
                        end={it.to === "/"}
                        onClick={onNavigate}
                        data-testid={`nav-${it.key}`}
                        className={({ isActive }) =>
                            `block py-2 font-display text-[19px] leading-[1.2] tracking-[-0.005em] transition-colors duration-500 ${
                                isActive
                                    ? "text-[color:var(--navy)] italic"
                                    : "text-[color:var(--navy)]/70 hover:text-[color:var(--navy)]"
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <span className="inline-flex items-center gap-3">
                                <span
                                    aria-hidden
                                    className={`h-px transition-all duration-500 ${
                                        isActive ? "w-5 bg-[color:var(--gold)]" : "w-0 bg-transparent"
                                    }`}
                                />
                                <span>{pick3(t3.nav[it.key], lang)}</span>
                            </span>
                        )}
                    </NavLink>
                </li>
            ))}
        </ul>
    );
}

function LangSelector() {
    const { lang, setLang } = useLanguage();
    return (
        <div className="flex items-center gap-1 font-display text-[14px] tracking-[0.02em]" data-testid="lang-selector">
            {LANGS.map((code, i) => (
                <React.Fragment key={code}>
                    <button
                        type="button"
                        data-testid={`lang-toggle-${code}`}
                        onClick={() => setLang(code)}
                        aria-pressed={lang === code}
                        aria-label={`Switch language to ${code.toUpperCase()}`}
                        className={`px-1.5 py-1 transition-colors duration-500 ${
                            lang === code
                                ? "text-[color:var(--navy)] italic"
                                : "text-[color:var(--navy)]/55 hover:text-[color:var(--navy)]"
                        }`}
                    >
                        {code.toUpperCase()}
                    </button>
                    {i < LANGS.length - 1 && (
                        <span className="text-[color:var(--line-strong)]" aria-hidden>·</span>
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

function LighthouseMark({ onNavigate }) {
    const { lang } = useLanguage();
    return (
        <Link
            to="/"
            onClick={onNavigate}
            data-testid="sidebar-brand"
            aria-label={pick3(t3.brand, lang)}
            className="block px-6 pt-10 pb-8"
        >
            <img
                src="/brand/lighthouse.png"
                alt=""
                aria-hidden="true"
                className="w-[86px] md:w-[96px] h-auto select-none"
                draggable="false"
            />
        </Link>
    );
}

function SidebarContents({ onNavigate }) {
    const { lang } = useLanguage();
    return (
        <div className="h-full flex flex-col">
            <LighthouseMark onNavigate={onNavigate} />

            <div className="px-6 flex-1 overflow-y-auto">
                <NavList items={PRIMARY} onNavigate={onNavigate} lang={lang} />
                <div className="my-6 h-px bg-[color:var(--line)]" aria-hidden />
                <NavList items={SECONDARY} onNavigate={onNavigate} lang={lang} />
                <div className="my-6 h-px bg-[color:var(--line)]" aria-hidden />
                <NavList items={TERTIARY} onNavigate={onNavigate} lang={lang} />
            </div>

            <div className="px-6 pb-8 pt-6">
                <LangSelector />
                <nav aria-label="Legal" className="mt-6 flex flex-wrap gap-x-3 gap-y-1 font-display italic text-[12px] text-[color:var(--navy)]/55">
                    <Link to="/legal-notice" data-testid="sidebar-legal-notice" className="hover:text-[color:var(--navy)] transition-colors">{
                        lang === "es" ? "Aviso" : lang === "nl" ? "Colofon" : lang === "fr" ? "Mentions" : "Legal"
                    }</Link>
                    <span aria-hidden className="text-[color:var(--line-strong)]">·</span>
                    <Link to="/privacy" data-testid="sidebar-privacy" className="hover:text-[color:var(--navy)] transition-colors">{
                        lang === "es" ? "Privacidad" : lang === "nl" ? "Privacy" : lang === "fr" ? "Confidentialité" : "Privacy"
                    }</Link>
                    <span aria-hidden className="text-[color:var(--line-strong)]">·</span>
                    <Link to="/cookies" data-testid="sidebar-cookies" className="hover:text-[color:var(--navy)] transition-colors">Cookies</Link>
                </nav>
            </div>
        </div>
    );
}

export default function SidebarNav() {
    const [open, setOpen] = useState(false);
    const { lang } = useLanguage();
    const location = useLocation();

    // Auto-close mobile drawer on route change
    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <>
            {/* Desktop sidebar */}
            <aside
                data-testid="sidebar-nav"
                className="hidden lg:block fixed inset-y-0 left-0 z-40 border-r border-[color:var(--line)] bg-[color:var(--ivory)]"
                style={{ width: "var(--sidebar-w)" }}
                aria-label={pick3(t3.brand, lang)}
            >
                <SidebarContents />
            </aside>

            {/* Mobile top bar */}
            <div
                data-testid="mobile-topbar"
                className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-5 py-3 bg-[color:var(--ivory)]/95 backdrop-blur-md border-b border-[color:var(--line)]"
            >
                <Link to="/" data-testid="mobile-brand" aria-label={pick3(t3.brand, lang)}>
                    <img src="/brand/lighthouse.png" alt="" aria-hidden="true" className="h-10 w-auto" draggable="false" />
                </Link>
                <button
                    type="button"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    aria-label={open ? pick3(t3.nav.close, lang) : pick3(t3.nav.menu, lang)}
                    className="font-display italic text-[16px] text-[color:var(--navy)] px-2 py-1"
                >
                    {open ? pick3(t3.nav.close, lang) : pick3(t3.nav.menu, lang)}
                </button>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="mobile-drawer"
                    className="lg:hidden fixed inset-0 z-30 bg-[color:var(--ivory)] pt-14 overflow-y-auto"
                    role="dialog"
                    aria-modal="true"
                >
                    <SidebarContents onNavigate={() => setOpen(false)} />
                </div>
            )}
        </>
    );
}
